import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { getJitteredValue } from './contentUtils'
import { getDomPromise, throwIfTimeout, wait } from '../interruptiblePromises'
import { getElement, getOptionalElement } from '../utils'
import { evaluateXpath } from '../xpath'

enum RedditNotificationModalOption {
  ALL_ON = 'All on',
  INBOX = 'Inbox',
  ALL_OFF = 'All off',
}

async function getSettingsRow(
  section: string,
  dataTestId: string,
): Promise<HTMLElement | null> {
  const settingsSection = await getElement<HTMLElement>(
    document,
    `//${section}`,
  )

  if (!settingsSection?.shadowRoot) {
    remoteLog.debug(
      `getSettingsRow: Couldn't find settings-notifications-section`,
    )
    return null
  }

  for (const child of settingsSection.shadowRoot.children) {
    const row = evaluateXpath<HTMLLabelElement>(
      child,
      `./div[@data-testid="${dataTestId}"]/label`,
    )[0]
    if (row) {
      return row
    }
  }

  remoteLog.debug(
    `getSettingsRow: Couldn't find row with data-testid ${dataTestId}`,
  )
  return null
}

function getModal(): HTMLElement | undefined {
  return evaluateXpath<HTMLElement>(document, '//rpl-modal-card')[0]
}

async function getModalOptionByXpath(
  xpath: string,
): Promise<HTMLSpanElement | null> {
  const modal = getModal()

  if (!modal) {
    remoteLog.debug('getModalOptionByXpath: No modal found')
    return null
  }

  return getOptionalElement<HTMLSpanElement>(modal, xpath, {
    timeout: 1000,
  })
}

const modalIsOpen = () => getModal() !== undefined
const modalIsClosed = () => getModal() === undefined

function waitForModalToBe(action: 'open' | 'closed') {
  const check = action === 'open' ? modalIsOpen : modalIsClosed
  return getDomPromise(document, check)
}

/**
 * Open the modal for the given setting
 * @param section - The name of the page section
 * @param dataTestId - The data-testid attribute of the setting
 */
async function openModalForSetting(
  section: string,
  dataTestId: string,
): Promise<void> {
  const row = await getSettingsRow(section, dataTestId)

  if (!row) {
    throw new Error(`Could not find setting row for ${dataTestId}`)
  }

  const isModalOpen = waitForModalToBe('open')
  row.click()
  await isModalOpen
}

/**
 * Get the currently selected option from the modal
 */
async function getSelectedModalOption(): Promise<RedditNotificationModalOption | null> {
  const span = await getModalOptionByXpath(
    './/div[@aria-selected="true"]//div/span',
  )

  if (!span) {
    return null
  }

  return span.textContent as RedditNotificationModalOption
}

/**
 * Click the option in the modal with the given text
 * @param option - The option to click
 */
async function clickModalOption(
  option: RedditNotificationModalOption,
): Promise<void> {
  const span = await getModalOptionByXpath(`.//span[text()="${option}"]`)

  if (!span) {
    throw new Error(`Could not find option with label text ${option}`)
  }

  span.click()
}

/**
 * Close the modal via the X or the cancel buttons
 */
async function closeModal(
  options: {
    timeoutMs?: number
    removeModalManually: boolean
  } = {
    removeModalManually: false,
  },
): Promise<void> {
  let timeoutMs = options.timeoutMs
  if (timeoutMs === null || timeoutMs === undefined) {
    timeoutMs = 1000
  }

  const modal = getModal()

  if (!modal) {
    remoteLog.debug('closeModal: No modal found')
    return
  }

  let closeButton = await getOptionalElement<HTMLButtonElement>(
    modal,
    './button[@slot="close-button"]',
    { timeout: 1000 },
  )

  if (!closeButton) {
    remoteLog.debug(
      'closeModal: Could not find close button, trying cancel button',
    )
    closeButton = await getOptionalElement<HTMLButtonElement>(
      modal,
      './button[@slot="secondary-button"]',
      { timeout: 1000 },
    )
  }

  if (!closeButton) {
    throw new Error('Could not find a button to close the modal')
  }

  const isModalClosed = waitForModalToBe('closed')

  closeButton.click()

  if (options.removeModalManually) {
    wait(Math.min(timeoutMs / 2, 100)).then(() => {
      modal.remove()
    })
  }

  await Promise.race([
    isModalClosed,
    throwIfTimeout(`Modal did not close (waited ${timeoutMs})`, timeoutMs),
  ])
}

/**
 * Save the current setting (also closes the modal)
 */
async function clickSave(): Promise<void> {
  const modal = getModal()

  if (!modal) {
    remoteLog.debug('clickSave: No modal found')
    return
  }

  const saveButton = await getElement<HTMLButtonElement>(
    modal,
    './button[@slot="primary-button"]',
    { timeout: 1000 },
  )

  const isModalClosed = waitForModalToBe('closed')
  await wait(getJitteredValue(750, 250))
  saveButton.click()
  await isModalClosed
}

/**
 * Get the currently selected option for a setting
 */
async function getSettingOption(
  section: string,
  dataTestId: string,
  options: {
    leaveModalOpen: boolean
    removeModalManually: boolean
  },
): Promise<RedditNotificationModalOption | null> {
  await openModalForSetting(section, dataTestId)
  const selectedOption = await getSelectedModalOption()

  if (!options.leaveModalOpen) {
    await closeModal(options)
  }

  return selectedOption
}

/**
 * Update the setting to the given option
 */
async function setSettingOption(
  section: string,
  dataTestId: string,
  option: RedditNotificationModalOption,
  modalLeftOpen: boolean = false,
): Promise<void> {
  if (!modalLeftOpen) {
    await openModalForSetting(section, dataTestId)
  }
  await clickModalOption(option)
  await clickSave()
}

export {
  closeModal,
  getSettingOption,
  setSettingOption,
  RedditNotificationModalOption,
}
